<template>
    <div>
        <div class="_calendar">
            <table class="table-days">
                <thead>
                    <tr>
                        <td v-for="(day, i) in weekDayStrings" :key="i">
                            {{ day }}
                        </td>
                    </tr>
                </thead>
            </table>
            <table
                class="table-events no-theme"
                :class="['rows-' + weekCount, 'font-size-' + form.font_size]"
            >
                <tbody>
                    <tr v-for="(week, i) in dates" :key="i">
                        <td
                            :class="{
                                'hide-date':
                                    date_info.date.month() + 1 !== month
                            }"
                            :data-date="date_info.date.format('YYYYMMDD')"
                            :key="date_info.date.format('YYYYMMDD')"
                            :style="{
                                cursor: date_info.editable
                                    ? 'pointer'
                                    : 'default'
                            }"
                            :title="getDateTooltipTitle(date_info)"
                            @click="onClickDate(date_info)"
                            v-for="date_info in week"
                        >
                            <span class="date">{{
                                date_info.date.date()
                            }}</span>
                            <div
                                :class="{
                                    'maxed-out': isMaxedOut(date_info.date)
                                }"
                                class="cell"
                            >
                                <template
                                    v-if="date_info.date.month() + 1 === month"
                                >
                                    <div
                                        :class="{
                                            'first-event': i === 0,
                                            'all-day': event.is_all_day
                                        }"
                                        :style="getEventStyle(event)"
                                        class="description"
                                        v-for="(event, i) in date_info.events"
                                        :key="i"
                                    >
                                        <div class="time float-left text-right">
                                            <span v-if="!event.is_all_day"
                                                >{{
                                                    formatEventTime(event)
                                                }}&nbsp;&nbsp;</span
                                            >
                                        </div>
                                        <div
                                            class="description-lines float-right event-title"
                                            v-html="
                                                getEventDisplayText(
                                                    event,
                                                    i === 0,
                                                    date_info.overlaps_date_number
                                                )
                                            "
                                        ></div>
                                        <div class="clearfix"></div>
                                    </div>
                                </template>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <font-calibration-tool
            v-if="isCalibrationToolAllowed && showCalibrationTool"
            :boldMap="boldMap"
            @calibration-results="handleCalibrationResults"
            @adjustments-applied="handleAdjustmentsApplied"
            @adjustments-restored="checkDateLines"
        />
        <el-button
            v-if="isCalibrationToolAllowed"
            @click="toggleCalibrationTool"
            size="mini"
            style="position: fixed; bottom: 10px; right: 110px"
            type="primary"
        >
            {{ showCalibrationTool ? "Hide" : "Show" }} Calibration Tool
        </el-button>
        <!-- <el-button
            v-if="isCalibrationToolAllowed"
            @click="refreshEventDisplay"
            size="mini"
            style="position: fixed; bottom: 10px; right: 10px"
            type="primary"
        >
            Refresh Text Display
        </el-button> -->
    </div>
</template>

<script>
import FontCalibrationTool from "@/components/FontCalibrationTool.vue";
import * as Fonts from "@/constants/calendar-fonts";
import * as MonthlyWeek from "@/constants/monthly-weeks";
import * as RepeatInterval from "@/constants/repeat-intervals";
import * as ROLES from "@/constants/roles";
import event_availability_mixin from "@/mixins/events/availability";
import FontService from "@/services/font-service";
import CalendarUtils from "@/utils/calendar";
import FontCalibration from "@/utils/font-calibration";
import { cloneDeep, filter, includes, sortBy, sumBy } from "lodash";
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import Environment from "@/utils/Environment";

export default {
    mixins: [event_availability_mixin],
    components: {
        FontCalibrationTool
    },
    props: {
        events: {
            type: Array,
            required: true
        },
        month: {
            type: Number,
            required: true
        },
        year: {
            type: Number,
            required: true
        },
        form: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            Fonts,
            maxedOutDates: [],
            weeks: [],
            results: null,
            showCalibrationTool: false
        };
    },
    mounted() {
        // Force recalculation of line wrapping when component is mounted
        Vue.nextTick(() => {
            this.refreshEventDisplay();
            //this.debugEventWrapping();
        });
    },
    created() {
        // Load font adjustments if not already loaded
        if (!this.fontAdjustments) {
            this.fetchFontAdjustments();
        }
    },
    computed: {
        ...mapState({
            weekDayStrings: (state) => state.enums.weekday_strings,
            boldMap: (state) => state.config.settings.font.bold_map,
            fontAdjustments: (state) => state.fontAdjustments.adjustments
            // userRoles: (state) =>
            //     state.auth && state.auth.user ? state.auth.user.roles || [] : []
        }),
        ...mapGetters(["userRoles", "hasAnyRole"]),
        calendarCalculator() {
            return this.$parent.calendarCalculator;
        },
        dates() {
            let start_date = moment([this.year, this.month - 1]).startOf(
                "month"
            );
            let end_date = start_date.clone().endOf("month");
            let dates = [];
            let week_dates = [];
            this.calendarCalculator.dates.forEach((date) => {
                let events = this.getEventsForDate(date);
                let date_info = {
                    date: date.clone(),
                    events,
                    editable: date.month() + 1 === this.month,
                    overlaps_date_number:
                        this.calendarCalculator.firstLineOverlapsDateNumber(
                            events
                        )
                };
                week_dates.push(date_info);
                date = date.clone().add(1, "day");
                if (date.day() === 0 || date.isSameOrAfter(end_date)) {
                    dates.push(week_dates);
                    week_dates = [];
                }
            });
            return dates;
        },
        weekCount() {
            return this.calendarCalculator.week_count;
        },
        isDevelopment() {
            return Environment.isDevelopment();
        },
        isCalibrationToolAllowed() {
            // Show in development OR for staff users in production
            return (
                Environment.isDevelopment() || this.hasAnyRole(ROLES.MAIN_ROLES)
            );
        }
    },
    methods: {
        ...mapActions("fontAdjustments", [
            "fetchFontAdjustments",
            "updateAdjustments"
        ]),
        onClickDate(date_info) {
            if (!date_info.editable) {
                return;
            }
            this.$emit("click-date", date_info.date.clone(), date_info.events);
        },
        getEventsForDate(date) {
            let events = filter(this.events, (event) => {
                switch (event.repeat_interval) {
                    case RepeatInterval.NO_REPEAT:
                        return (
                            event.date.format("YYYYMMDD") ===
                            date.format("YYYYMMDD")
                        );
                    case RepeatInterval.DAILY:
                        return true;
                    case RepeatInterval.EVERY_WEEKDAY:
                        return includes([1, 2, 3, 4, 5], date.day());
                    case RepeatInterval.WEEKLY:
                        return event[
                            "weekly_" + this.weekDayStrings[date.day()]
                        ];
                    case RepeatInterval.BIWEEKLY:
                        return event[
                            "weekly_" + this.weekDayStrings[date.day()]
                        ]
                            ? this.checkDateBiWeekly(date, event)
                            : false;
                    case RepeatInterval.MONTHLY:
                        if (event.monthly_day === null) {
                            return false;
                        }
                        if (event.monthly_week === "last") {
                            let last_day = moment([
                                this.year,
                                this.month - 1
                            ]).endOf("month");
                            while (
                                last_day.day() !== parseInt(event.monthly_day)
                            ) {
                                last_day.subtract(1, "day");
                            }
                            return last_day.isSame(date, "day");
                        }
                        return moment
                            .recur()
                            .every(event.monthly_day)
                            .daysOfWeek()
                            .every(
                                MonthlyWeek[event.monthly_week.toUpperCase()] -
                                    1
                            )
                            .weeksOfMonthByDay()
                            .matches(date);
                    case RepeatInterval.YEARLY:
                        return (
                            date.month() === event.date.month() &&
                            date.date() === event.date.date()
                        );
                    default:
                        return false;
                }
            });
            events = this.filterDeletedEvents(events, date);
            return sortBy(events, (event) =>
                event.time ? moment(event.time).format("HH:mm:ss") : "00:00:00"
            );
        },
        getEventStyle(event) {
            let font_name = CalendarUtils.getValidFontName(
                this.form.font,
                event.is_bold
            );
            const fontWeight =
                FontService.getFontWeightFromCSS(font_name) || "normal";
            return {
                "font-family": font_name,
                "font-size": this.form.font_size + "pt",
                color: event.color,
                "font-weight": fontWeight
            };
        },
        formatEventTime(event) {
            return moment(event.time).format("h:mm");
        },
        /**
         * Get the event display text in html format.
         *
         * @param {Object} event
         * @param {Boolean} first_event True if the event is the first line of the date box.
         * @param {Boolean} first_line_overlaps_date
         * @returns {string}
         */
        getEventDisplayText(
            event,
            first_event = false,
            first_line_overlaps_date = false
        ) {
            let html = "";
            CalendarUtils.getEventDescriptionLines(
                event,
                this.form.font,
                this.form.font_size,
                first_event && first_line_overlaps_date
            ).forEach((line) => {
                if (html !== "") {
                    html += "<br>";
                }
                html += Utils.htmlentities(line);
            });
            return html;
        },
        isMaxedOut(date) {
            return includes(
                this.maxedOutDates,
                moment(date).format("YYYY-MM-DD")
            );
        },
        refreshCalendarDisplay(options = {}) {
            const settings = {
                forceFullRefresh: false,
                caller: "unknown",
                ...options
            };

            console.log(`Calendar refresh called by: ${settings.caller}`);

            // Return a promise so callers can wait for completion
            return new Promise((resolve) => {
                // First ensure fonts are fully loaded
                Utils.ensureFontsLoaded().then(() => {
                    // Always clear the cache for consistent results
                    CalendarUtils.clearDescriptionLinesCache();

                    // Reset cached description lines on all events
                    if (this.events) {
                        this.events.forEach((event) => {
                            delete event.description_lines;
                        });
                    }

                    // Force a proper render update
                    this.$forceUpdate();

                    // Wait for render to complete before recalculating
                    Vue.nextTick(() => {
                        // Calculate maxed out dates with fresh data
                        this.calculateMaxedOutDates();

                        // Final update to refresh the view with new maxed out data
                        this.$forceUpdate();

                        // Let caller know we're done
                        resolve(this.maxedOutDates);
                    });
                });
            });
        },
        calculateMaxedOutDates() {
            // Clear the array at the beginning of calculation
            this.maxedOutDates = [];

            let max_lines;
            if (this.form.is_weekly) {
                // Weekly calculation logic (same as before)
                max_lines = 21; // 7 lines per column
                let max_lines_per_column = 7;

                this.dates.forEach((row) => {
                    row.forEach((date_info) => {
                        if (date_info.date.month() + 1 !== this.form.month) {
                            return;
                        }
                        let column_line_count = 0;
                        let column = 1;

                        date_info.events.forEach((event) => {
                            column_line_count += event.description_lines
                                ? event.description_lines.length
                                : 0;
                            if (column_line_count > max_lines_per_column) {
                                column_line_count =
                                    event.description_lines.length;
                                column++;
                            } else {
                                if (
                                    column_line_count === max_lines_per_column
                                ) {
                                    column_line_count = 0;
                                    column++;
                                }
                            }
                        });
                        if (column > 3 && column_line_count > 0) {
                            this.maxedOutDates.push(
                                date_info.date.format("YYYY-MM-DD")
                            );
                        }
                    });
                });
            } else {
                // Monthly calculation logic (same as before)
                max_lines = this.calendarCalculator._max_lines;
                this.dates.forEach((row) => {
                    row.forEach((date_info) => {
                        if (date_info.date.month() + 1 !== this.form.month) {
                            return;
                        }
                        // Ensure all events have their description_lines calculated
                        date_info.events.forEach((event) => {
                            if (!event.description_lines) {
                                event.description_lines =
                                    CalendarUtils.getEventDescriptionLines(
                                        event,
                                        this.form.font,
                                        this.form.font_size,
                                        false
                                    );
                            }
                        });
                        let total_lines = sumBy(date_info.events, (e) =>
                            e.description_lines ? e.description_lines.length : 0
                        );
                        if (total_lines > max_lines) {
                            this.maxedOutDates.push(
                                date_info.date.format("YYYY-MM-DD")
                            );
                        }
                    });
                });
            }

            return this.maxedOutDates;
        },
        checkDateLines() {
            return this.refreshCalendarDisplay({ caller: "checkDateLines" });
        },
        refreshEventDisplay() {
            return this.refreshCalendarDisplay({
                caller: "refreshEventDisplay"
            });
        },
        ensureFontAndRefreshEvents() {
            return this.refreshCalendarDisplay({
                caller: "ensureFontAndRefreshEvents",
                forceFullRefresh: true
            });
        },
        getDateTooltipTitle(date_info) {
            if (!date_info.editable) {
                return null;
            }
            if (this.isMaxedOut(date_info.date)) {
                return `This date has exceeded the maximum lines. Please click to edit events.`;
            }
            return "Click to edit events";
        },
        /**
         * @param date
         * @returns {boolean}
         */
        checkDateBiWeekly(date, event) {
            let new_event = cloneDeep(event);
            let new_event_date = new_event.date;
            if (new_event.parent) {
                new_event_date = moment(new_event.parent.event_date);
            }
            let days = date.diff(new_event_date, "days");
            let weeks = 0;

            let currentDateEvent =
                event["weekly_" + this.weekDayStrings[new_event_date.day()]];

            if (days >= 0) {
                weeks = Math.ceil(days / 7);
                if (currentDateEvent && date.day() == new_event_date.day()) {
                    if (weeks % 2 == 0) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    if (weeks % 2 != 0) {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else {
                return false;
            }
        },
        runCalibration() {
            // Sample texts that are problematic
            const sampleTexts = [
                "November Birthday Dessert Bar (DR)",
                "November Birthday",
                "Dessert Bar (DR)",
                "Meeting with John Smith at Office"
            ];

            this.results = FontCalibration.runCalibration(sampleTexts);

            // Generate updated fontAdjustments
            const fontAdjustments =
                FontCalibration.generateSizeSpecificFontAdjustments(
                    this.results
                );
            console.log("Updated fontAdjustments:", fontAdjustments);
        },

        handleAdjustmentsApplied() {
            // Update line wrapping with new adjustments
            this.checkDateLines();
        },

        debugEventWidth(event) {
            const fontName = CalendarUtils.getValidFontName(
                this.form.font,
                event.is_bold
            );
            const text = event.description;
            const width = Utils.getTextWidth(
                text,
                fontName,
                this.form.font_size,
                CalendarUtils.getFontWeight(fontName)
            );
            const maxWidth = CalendarUtils.getEventDescriptionMaxWidth(
                event,
                this.form.font,
                this.form.font_size
            );
            console.log(
                `Event: "${text}", Width: ${width}px, MaxWidth: ${maxWidth}px, Difference: ${
                    width - maxWidth
                }px`
            );
        },
        debugEventWrapping() {
            const problemEvents = [
                "Worship service & Communion w/ Pastor Luke-L",
                "Mexican Train Dominoes-SH",
                "Bible Study W/ Scott-Library"
            ];

            const font = this.form.font;
            const fontSize = this.form.font_size;

            problemEvents.forEach((text) => {
                // Clear cache for this test
                CalendarUtils.clearDescriptionLinesCache();

                // Create test event
                const testEvent = {
                    description: text,
                    is_bold: false,
                    is_all_day: false
                };

                // Get the wrapped lines
                const lines = CalendarUtils.getEventDescriptionLines(
                    testEvent,
                    font,
                    fontSize
                );

                console.log(`Event: "${text}"\nWrapped as:`, lines);

                // Calculate max width
                const maxWidth = CalendarUtils.getEventDescriptionMaxWidth(
                    testEvent,
                    font,
                    fontSize
                );

                // Show the estimated line count that triggers getBalancedLines
                const totalWidth = Utils.getTextWidth(
                    text,
                    CalendarUtils.getValidFontName(font, false),
                    fontSize,
                    CalendarUtils.getFontWeight(font)
                );
                const estimatedLines = Math.ceil(totalWidth / maxWidth);

                console.log(
                    `Max width: ${maxWidth}px, Total width: ${totalWidth}px, Estimated lines: ${estimatedLines}`
                );
            });
        },
        // Update method to provide admin access to font calibration
        handleCalibrationResults(results) {
            if (results && confirm("Save these font adjustment values?")) {
                this.updateAdjustments(results);
                this.checkDateLines();
            }
        },
        toggleCalibrationTool() {
            this.showCalibrationTool = !this.showCalibrationTool;
        }
    }
};
</script>

<style lang="scss" scoped>
._calendar {
    width: 1632px;
    height: 1056px;
    position: relative;

    table {
        &.table-events {
            position: absolute;
            /* Reduce this value to close the gap */
            top: 20px; /* Changed from 67px */
            left: 35px;

            tbody td {
                width: 219px;
                height: 154px;
                max-width: 219px;
                overflow: hidden;

                .date {
                    display: none;
                }

                .cell {
                    max-height: 154px;
                    overflow: hidden;
                    max-width: 219px;
                    padding: 2px;

                    .description {
                        white-space: nowrap;
                        margin-top: 1px;

                        &:first-of-type {
                            margin-top: 10px;
                        }

                        .event-title {
                            line-height: inherit;
                        }

                        &.all-day {
                            .time {
                                display: none;
                            }

                            .description-lines {
                                float: left;
                                margin-left: 17px;
                                text-align: center;
                            }
                        }
                    }
                }
            }

            &.font-size-12 {
                line-height: 12pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 50px;
                                max-width: 50px;
                            }

                            .description-lines {
                                width: 158px;
                            }
                        }
                    }
                }

                &.rows-6 {
                    tbody td {
                        .cell {
                            .description {
                                margin-top: 6px;
                            }
                        }
                    }
                }
            }

            &.font-size-11 {
                line-height: 11pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 50px;
                                max-width: 50px;
                            }

                            .description-lines {
                                width: 158px;
                            }
                        }
                    }
                }
            }

            &.font-size-10 {
                line-height: 10pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 42px;
                                max-width: 42px;
                            }

                            .description-lines {
                                width: 165px;
                            }
                        }
                    }
                }

                &.rows-6 {
                    tbody td {
                        .cell {
                            .description {
                                margin-top: 1px;
                            }
                        }
                    }
                }
            }

            &.font-size-9 {
                line-height: 9pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 42px;
                                max-width: 42px;
                            }

                            .description-lines {
                                width: 165px;
                            }
                        }
                    }
                }
            }

            &.font-size-8 {
                line-height: 8pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 35px;
                                max-width: 35px;
                            }

                            .description-lines {
                                width: 174px;
                            }
                        }
                    }
                }

                &.rows-6 {
                    tbody td {
                        .cell {
                            .description {
                                margin-top: 1px;
                            }
                        }
                    }
                }
            }

            &.font-size-7 {
                line-height: 7pt;

                tbody td {
                    .cell {
                        .description {
                            margin-top: 1px;

                            .time {
                                width: 35px;
                                max-width: 35px;
                            }

                            .description-lines {
                                width: 174px;
                            }
                        }
                    }
                }
            }

            &.rows-5 {
                top: 54px;

                tbody td {
                    height: 167px;
                }

                tbody td .cell {
                    /*height: 167px;*/
                    max-height: 167px;
                }

                &.font-size-12 {
                    line-height: 12pt;
                }

                &.font-size-11 {
                    line-height: 11pt;
                }

                &.font-size-10 {
                    line-height: 10pt;
                }

                &.font-size-9 {
                    line-height: 9pt;
                }

                &.font-size-8 {
                    line-height: 8pt;
                }


            }

            &.rows-6 {
                top: 54px;

                &.font-size-12 {
                    line-height: 12pt;
                }
                &.font-size-11 {
                    line-height: 11pt;
                }
                &.font-size-10 {
                    line-height: 10pt;
                }

                &.font-size-9 {
                    line-height: 9pt;
                }

                &.font-size-8 {
                    line-height: 8pt;
                }

                &.font-size-7 {
                    line-height: 7pt;
                }
            }
        }

        &.table-days {
            /* Change to position sticky for scrolling behavior */
            position: sticky;
            top: 0; /* Stick to top of viewport when scrolling */
            left: 35px;
            /* Add z-index to keep it above other content */
            z-index: 10;
            /* Add background to prevent content showing through */
            background-color: white;

            thead td {
                width: 219px;
                max-width: 219px;
                height: 36px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                font-size: 24px;
            }
        }

        &.table-events.no-theme tbody td {
            .cell {
                position: relative;

                &.maxed-out {
                    background-color: pink;
                }
            }

            border: 1px solid black;
            position: relative;

            &.hide-date {
                border: none;

                .cell.maxed-out {
                    background-color: transparent;
                }
            }

            .date {
                display: block;
                position: absolute;
                top: 5px;
                right: 5px;
                text-align: right;
                width: 100%;
                font-weight: bold;
                font-size: 19px;
                z-index: 1;
            }

            &.hide-date .date {
                display: none;
            }
        }
    }
}
</style>
